import PeersSearch from "../PeersSearch";
import IndustryClassPeers from "./IndustryClassPeers";
import NAICSPeers from "./NAICSPeers";
import PeerRateChart from "./PeerRateChart";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import {
  Checkbox,
  Grid,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Tooltip,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import ButtonCommon from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import Disabled from "components/common/Disabled";
import ExposureSelector from "components/common/ExposureSelector";
import GoodBadTableCell from "components/common/GoodBadTableCell";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import SubmissionLink from "components/common/SubmissionLink";
import Visible from "components/common/Visible";
import PeerSelectorDialog from "components/peers/review/PeerSelectorDialog";
import * as config from "config";
import * as labels from "config.labels";
import { Fragment } from "react";
import { useState } from "react";
import { connect, useSelector } from "react-redux";
import * as peersActions from "store/actions/peers/peersActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as peersSelectors from "store/selectors/peers/peersSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const PercentFormatted = (props) => (
  <GoodBadTableCell
    suffix={"%"}
    decimalScale={2}
    fixedDecimalScale
    {...props}
  />
);

const DollarFormatted = (props) => <GoodBadTableCell prefix={"$"} {...props} />;

const ReviewComponent = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [addPeerDialogOpen, setAddPeerDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const componentVisibility =
    useSelector(userSelectors.selectUserConfig)?.componentVisibility ?? {};

  const columns = [
    {
      key: "delete",
      header: { title: "", tooltip: "", align: "left" },
      Cell: ({ isLast, layerIndex, peerIndex, submission }) => (
        <TableCell style={isLast ? {} : { borderBottom: "none" }}>
          {layerIndex > 0 || peerIndex === 0 ? (
            ""
          ) : (
            <IconButton
              icon={DeleteIcon}
              onClick={() => props.deletePeer(submission.filename)}
            />
          )}
        </TableCell>
      ),
      isPrefixColumn: true,
    },
    {
      key: "insured",
      header: { title: "Insured", tooltip: "", align: "left" },
      Cell: ({ isLast, layerIndex, submission, peerIndex }) => (
        <TableCell style={isLast ? {} : { borderBottom: "none" }}>
          {layerIndex > 0 ? (
            <></>
          ) : peerIndex === 0 ? (
            <span>{submission.insured}</span>
          ) : (
            <SubmissionLink
              insured={submission.insured}
              submissionId={submission.filename.slice(0, -5)}
            />
          )}
        </TableCell>
      ),
      isPrefixColumn: true,
    },
    {
      key: "inception",
      header: { title: "Inception", tooltip: "", align: "left" },
      Cell: ({ isLast, layerIndex, submission }) => (
        <TableCell
          className={"tablecell-inception"}
          style={isLast ? {} : { borderBottom: "none" }}
        >
          {layerIndex > 0 ? "" : submission.inception}
        </TableCell>
      ),
      isPrefixColumn: true,
    },
    {
      key: "isPriced",
      header: {
        title: "Price",
        tooltip: "Select which layers to use in pricing",
        align: "center",
      },
      Cell: ({ peerIndex, submission, layer }) => (
        <TableCell>
          {peerIndex === 0 ? (
            <></>
          ) : (
            <Checkbox
              checked={layer.weighting > 0}
              onChange={(e) =>
                props.updatePricingSelection(
                  submission.filename,
                  layer.unfiltered_index,
                  e.target.checked ? 1 : 0
                )
              }
            />
          )}
        </TableCell>
      ),
    },
    {
      key: "fixedExposure",
      header: {
        title: props.fixedPeerMetricDefn.name,
        tooltip: "",
        align: "right",
      },
      Cell: ({ isLast, layerIndex, submission }) => (
        <GoodBadTableCell
          cellStyle={isLast ? {} : { borderBottom: "none" }}
          value={layerIndex > 0 ? "" : submission.fixedPeerMetricValue}
        />
      ),
    },
    {
      key: "exposure1",
      header: {
        title: props.exposureMetric?.name,
        tooltip: "",
        align: "right",
      },
      Cell: ({ isLast, layerIndex, submission }) => (
        <GoodBadTableCell
          cellStyle={isLast ? {} : { borderBottom: "none" }}
          value={layerIndex > 0 ? "" : submission.exposure}
        />
      ),
    },
    {
      key: "exposure2",
      header: {
        title: props.exposureMetric2?.name,
        tooltip: "",
        align: "right",
      },
      Cell: ({ isLast, layerIndex, submission }) => (
        <GoodBadTableCell
          cellStyle={isLast ? {} : { borderBottom: "none" }}
          value={layerIndex > 0 ? "" : submission.exposure2}
        />
      ),
    },
    {
      key: "lineSize",
      header: { title: "Our Limit", tooltip: "", align: "right" },
      Cell: ({ layer }) => <DollarFormatted value={layer.lineSize} />,
    },
    {
      key: "limit",
      header: { title: "Part Of", tooltip: "", align: "right" },
      Cell: ({ layer }) => <DollarFormatted value={layer.limit} />,
    },
    {
      key: "attachment",
      header: { title: "Attachment", tooltip: "", align: "right" },
      Cell: ({ layer }) => <DollarFormatted value={layer.attachment} />,
    },
    {
      key: "effectiveAttachment",
      header: { title: "Effective Attachment", tooltip: "", align: "right" },
      Cell: ({ layer }) => (
        <DollarFormatted value={layer.effectiveAttachment} />
      ),
      defaultVisibility: false,
    },
    {
      key: "grossPpm",
      header: { title: "Gross PPM", tooltip: "", align: "right" },
      Cell: ({ layer }) => <DollarFormatted value={layer.grossPPM} />,
    },
    {
      key: "tp",
      header: {
        title: "Target Price (TP)",
        tooltip: config.DEFINITION_TP,
        align: "right",
      },
      Cell: ({ layer }) => (
        <PercentFormatted value={layer.tp} good={(layer.tp ?? 100) - 100} />
      ),
    },
    {
      key: "tac",
      header: { title: "TAC", tooltip: config.DEFINITION_TAC, align: "right" },
      Cell: ({ layer }) => <PercentFormatted value={layer.totalAcquisition} />,
    },
  ].filter(
    ({ key, defaultVisibility }) =>
      componentVisibility?.[`peers.table.${key}`] ?? defaultVisibility ?? true
  );

  const prefixColumnCount = columns.findIndex(
    ({ isPrefixColumn }) => !isPrefixColumn
  );

  const LayerlessRow = ({
    submission,
    layer,
    layerIndex,
    isLast,
    peerIndex,
  }) => {
    return (
      <TableRow>
        {columns.slice(0, prefixColumnCount).map(({ key, Cell }) => (
          <Cell
            key={key}
            isLast={isLast}
            peerIndex={peerIndex}
            layerIndex={layerIndex}
            submission={submission}
            layer={layer}
          />
        ))}
        <TableCell colSpan={columns.length - prefixColumnCount}>
          {translateLabel(labels.PEER_GROUP_NO_LAYERS, {
            default: "No Layers with positive line size.",
          })}
        </TableCell>
      </TableRow>
    );
  };

  const LoadingRow = ({ submission, layer, layerIndex, isLast, peerIndex }) => {
    return (
      <TableRow>
        {columns.map(({ key, Cell }, i) =>
          i < prefixColumnCount ? (
            <Cell
              key={key}
              isLast={isLast}
              peerIndex={peerIndex}
              layerIndex={layerIndex}
              submission={submission}
              layer={layer}
            />
          ) : (
            <TableCell align={"right"} key={key}>
              <DefaultLoader
                style={{
                  display: "inline-block",
                  position: "relative",
                  top: "2px",
                }}
                size={16}
              />
            </TableCell>
          )
        )}
      </TableRow>
    );
  };

  const LayerRow = ({ submission, layer, layerIndex, isLast, peerIndex }) => {
    const theme = useTheme();
    return (
      <TableRow
        style={peerIndex === 0 ? { background: theme.brand.gray[800] } : {}}
      >
        {columns.map(({ key, Cell }) => (
          <Cell
            key={key}
            isLast={isLast}
            peerIndex={peerIndex}
            layerIndex={layerIndex}
            submission={submission}
            layer={layer}
          />
        ))}
      </TableRow>
    );
  };

  return (
    <>
      <Visible byTag={"<Comparison>.peerRateChart"} defaultVisible>
        <PeerRateChart />
      </Visible>
      <Component
        xs_width={12}
        title={translateLabel(labels.REVIEW_PEER_COMPARISON_TITLE, {
          default: "Peer Comparison",
        })}
        subtitle={translateLabel(labels.REVIEW_PEER_COMPARISON_SUBTITLE, {
          default: "Compare important metrics across similar insureds.",
        })}
        options={
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            {props.canRefreshPeers && (
              <Visible
                byTag={"<Comparison>.peerRefreshToCurrentYear"}
                defaultVisible={true}
              >
                <Disabled ifReadOnly>
                  <ButtonCommon
                    onClick={() => {
                      setRefreshing(true);
                      Promise.resolve(
                        props.refreshPeersToCurrentYear()
                      ).finally(() => setRefreshing(false));
                    }}
                    isLoading={refreshing}
                    isDisabled={refreshing}
                    startIcon={<RefreshRounded />}
                  >
                    {translateLabel(
                      labels.REVIEW_REFRESH_PEERS_TO_CURRENT_YEAR,
                      {
                        default: "Refresh to current year",
                      }
                    )}
                  </ButtonCommon>
                </Disabled>
              </Visible>
            )}
            <Visible byTag={"<Comparison>.peerSelector"} defaultVisible={false}>
              <Button
                onClick={() => setAddPeerDialogOpen(true)}
                color={"primary"}
                variant={"contained"}
                disableElevation
                sx={{ borderRadius: "20px" }}
              >
                {"+  Add Peers"}
              </Button>
            </Visible>
          </Stack>
        }
      >
        <TableContainer>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                {columns.map(({ header }, i) => {
                  const cell = (
                    <TableCell key={i} align={header.align}>
                      {header.title}
                    </TableCell>
                  );
                  if (header.tooltip) {
                    return (
                      <Tooltip key={i} title={header.tooltip}>
                        {cell}
                      </Tooltip>
                    );
                  }
                  return cell;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.peerData.map((submission, peerIndex) => {
                if (!submission.loaded) {
                  return (
                    <LoadingRow
                      key={peerIndex}
                      submission={submission}
                      layer={{}}
                      layerIndex={0}
                      isLast={true}
                      peerIndex={peerIndex}
                    />
                  );
                } else if (!submission?.layers?.length) {
                  return (
                    <LayerlessRow
                      key={peerIndex}
                      submission={submission}
                      layer={{}}
                      layerIndex={0}
                      isLast={true}
                      peerIndex={peerIndex}
                    />
                  );
                } else {
                  return (
                    <Fragment key={peerIndex}>
                      {submission.layers.map((layer, layerIndex) => (
                        <LayerRow
                          key={layerIndex}
                          submission={submission}
                          layer={layer}
                          layerIndex={layerIndex}
                          isLast={layerIndex + 1 === submission?.layers?.length}
                          peerIndex={peerIndex}
                        />
                      ))}
                    </Fragment>
                  );
                }
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Component>
      <Component
        title={translateLabel(labels.REVIEW_EXPOSURE_METRICS_TITLE, {
          default: "Exposure Metrics",
        })}
        subtitle={translateLabel(labels.REVIEW_EXPOSURE_METRICS_SUBTITLE, {
          default:
            "Select the exposure metrics to display in the Peer Comparison table.",
        })}
        lg_width={4}
        md_width={12}
      >
        <Grid container spacing={config.GRID_SPACING} direction={"column"}>
          <Grid item xs={12}>
            <ExposureSelector
              selectedExposureMetric={props.exposureMetric}
              exposureRows={props.exposureRows}
              updateChosenExposureMetric={props.updateChosenExposureMetric}
            />
          </Grid>
          <Grid item xs={12}>
            <ExposureSelector
              selectedExposureMetric={props.exposureMetric2}
              exposureRows={props.exposureRows}
              updateChosenExposureMetric={props.updateChosenExposureMetric2}
              title={"Exposure Metric 2"}
            />
          </Grid>
        </Grid>
      </Component>
      <Visible byTag={"<Comparison>.peerSearch"} defaultVisible={true}>
        <Component
          title={translateLabel(labels.REVIEW_ADD_PEERS_TITLE, {
            default: "Add Peers",
          })}
          subtitle={translateLabel(labels.REVIEW_ADD_PEERS_SUBTITLE, {
            default: "Search for peers to add to the Peer Comparison table.",
          })}
          lg_width={4}
          md_width={12}
        >
          <PeersSearch label={"Insured"} />
        </Component>
      </Visible>
      <Notes
        md_width={12}
        lg_width={4}
        update={props.updateNote}
        value={props.note}
      />
      <Visible byTag={"<Comparison>.peerIndustryClass"} defaultVisible={true}>
        <IndustryClassPeers />
      </Visible>
      <Visible byTag={"<Comparison>.peerNAICS"} defaultVisible={true}>
        <NAICSPeers />
      </Visible>
      <Visible byTag={"<Comparison>.peerSelector"} defaultVisible={false}>
        {addPeerDialogOpen && (
          <PeerSelectorDialog onClose={() => setAddPeerDialogOpen(false)} />
        )}
      </Visible>
    </>
  );
};

const mapDispatchToProps = {
  updateChosenExposureMetric: peersActions.updateChosenExposureMetric,
  updateChosenExposureMetric2: peersActions.updateChosenExposureMetric2,
  updatePricingSelection: peersActions.updatePricingSelection,
  deletePeer: peersActions.deletePeer,
  updateNote: peersActions.updatePeersNote,
  refreshPeersToCurrentYear: peersActions.refreshPeersToCurrentYear,
};

const mapStateToProps = (state) => {
  const fixedPeerMetricDefn = peersSelectors.selectFixedPeerMetricDefn(state);
  const hasPeers = (state.peers?.submissions?.length ?? 0) > 0;
  const isRenewal = !!programSelectors.getPriorSubmissionId(state);
  const canRefreshPeers = hasPeers && isRenewal;
  return {
    peerData: peersSelectors.getPeers(state),
    exposureMetric: state.peers.exposureMetric,
    exposureMetric2: state.peers.exposureMetric2,
    fixedPeerMetricDefn,
    exposureRows: exposureSelectors
      .getExposuresInAllSubmissionsUsed(state)
      .filter((x) => x.key !== fixedPeerMetricDefn.key),
    note: state.peers.note,
    canRefreshPeers,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewComponent);
